import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { axiosInstance } from '../../App';
import { 
  CreditCard, DollarSign, Calendar, Plus, Search, Eye, 
  Building, User, FileText, Receipt, Loader2, CheckCircle,
  Clock, AlertTriangle, Banknote, Wallet, ArrowDownCircle,
  Globe, HandCoins, Filter
} from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../../utils/dateUtils';

export default function PaymentsManagement() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_revenue: 0,
    completed_count: 0,
    pending_count: 0,
    online_count: 0,
    manual_count: 0,
    online_revenue: 0,
    manual_revenue: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  
  // Add Manual Payment states
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerJobs, setCustomerJobs] = useState([]);
  const [customerInvoices, setCustomerInvoices] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_mode: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    notes: ''
  });
  
  // Payment detail view
  const [showPaymentDetail, setShowPaymentDetail] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    fetchPayments();
    fetchCustomers();
    fetchStats();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await axiosInstance.get('/payments');
      setPayments(response.data);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get('/payments/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load payment stats');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axiosInstance.get('/customers');
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Failed to load customers');
    }
  };

  const handleCustomerSelect = async (customerId) => {
    setSelectedCustomerId(customerId);
    setSelectedJobId('');
    setSelectedInvoiceId('');
    setCustomerJobs([]);
    setCustomerInvoices([]);
    
    if (!customerId) return;
    
    try {
      const [jobsRes, invoicesRes] = await Promise.all([
        axiosInstance.get(`/customers/${customerId}/jobs`),
        axiosInstance.get(`/customers/${customerId}/invoices?status=open`)
      ]);
      setCustomerJobs(jobsRes.data || []);
      setCustomerInvoices(invoicesRes.data || []);
      
      // Auto-fill amount if invoice selected
      if (invoicesRes.data?.length === 1) {
        const inv = invoicesRes.data[0];
        setSelectedInvoiceId(inv.id);
        setPaymentForm(prev => ({
          ...prev,
          amount: inv.balance_due || inv.total_amount || ''
        }));
      }
    } catch (error) {
      console.error('Failed to load customer data');
    }
  };

  const handleInvoiceSelect = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    if (!invoiceId) {
      return;
    }
    const invoice = customerInvoices.find(i => i.id === invoiceId);
    if (invoice) {
      setPaymentForm(prev => ({
        ...prev,
        amount: invoice.balance_due || invoice.total_amount || ''
      }));
      // Also set job if invoice has one
      if (invoice.job_id) {
        setSelectedJobId(invoice.job_id);
      }
    }
  };

  const handleSubmitPayment = async () => {
    if (!selectedCustomerId) {
      toast.error('Please select a customer');
      return;
    }
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    setSubmitting(true);
    try {
      const customer = customers.find(c => c.id === selectedCustomerId);
      await axiosInstance.post('/payments', {
        customer_id: selectedCustomerId,
        job_id: selectedJobId || null,
        invoice_id: selectedInvoiceId || null,
        amount: parseFloat(paymentForm.amount),
        payment_mode: paymentForm.payment_mode,
        payment_date: paymentForm.payment_date,
        reference_number: paymentForm.reference_number,
        notes: paymentForm.notes,
        source: 'manual',
        entered_by: 'Admin'  // Would come from auth context
      });
      
      toast.success('Payment recorded successfully');
      setShowAddPayment(false);
      resetForm();
      fetchPayments();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedCustomerId('');
    setSelectedJobId('');
    setSelectedInvoiceId('');
    setCustomerJobs([]);
    setCustomerInvoices([]);
    setPaymentForm({
      amount: '',
      payment_mode: 'cash',
      payment_date: new Date().toISOString().split('T')[0],
      reference_number: '',
      notes: ''
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-500 text-white',
      pending: 'bg-yellow-500 text-white',
      failed: 'bg-red-500 text-white',
      refunded: 'bg-gray-500 text-white'
    };
    return colors[status] || 'bg-gray-500 text-white';
  };

  const getSourceBadge = (source) => {
    if (source === 'online') {
      return <Badge className="bg-blue-100 text-blue-700"><Globe className="w-3 h-3 mr-1" />Online</Badge>;
    }
    return <Badge className="bg-purple-100 text-purple-700"><HandCoins className="w-3 h-3 mr-1" />Manual</Badge>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', { timeZone: 'America/Phoenix',
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  // Filter payments
  const filteredPayments = payments.filter(p => {
    const matchesSearch = searchTerm === '' || 
      p.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.reference_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesSource = filterSource === 'all' || p.source === filterSource;
    
    return matchesSearch && matchesSource;
  });

  return (
    <div data-testid="payments-management">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Payment Management
          </h2>
          <p className="text-gray-600">Track online payments and record offline payments</p>
        </div>
        <Button 
          className="bg-[#595959] hover:bg-[#404040]"
          onClick={() => setShowAddPayment(true)}
          data-testid="add-payment-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Manual Payment
        </Button>
      </div>

      {/* Payment Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Total Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="total-revenue">
              {formatCurrency(stats.total_revenue)}
            </div>
            <p className="text-green-100 text-sm mt-1">{stats.completed_count} completed payments</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Online Payments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{formatCurrency(stats.online_revenue)}</div>
            <p className="text-blue-100 text-sm mt-1">{stats.online_count} transactions</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <HandCoins className="w-5 h-5" />
              Manual Payments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{formatCurrency(stats.manual_revenue)}</div>
            <p className="text-purple-100 text-sm mt-1">{stats.manual_count} entries</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Pending
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" data-testid="pending-payments">{stats.pending_count}</div>
            <p className="text-yellow-100 text-sm mt-1">Awaiting confirmation</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by customer, transaction ID, reference..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <Select value={filterSource} onValueChange={setFilterSource}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="online">Online Only</SelectItem>
                  <SelectItem value="manual">Manual Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <CardHeader>
          <CardTitle>Payment History</CardTitle>
          <CardDescription>All payment transactions</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#595959]" />
            </div>
          ) : filteredPayments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-medium text-gray-600">Payment ID</th>
                    <th className="text-left p-3 font-medium text-gray-600">Customer</th>
                    <th className="text-left p-3 font-medium text-gray-600">Job</th>
                    <th className="text-left p-3 font-medium text-gray-600">Invoice</th>
                    <th className="text-left p-3 font-medium text-gray-600">Date</th>
                    <th className="text-right p-3 font-medium text-gray-600">Amount</th>
                    <th className="text-center p-3 font-medium text-gray-600">Method</th>
                    <th className="text-center p-3 font-medium text-gray-600">Source</th>
                    <th className="text-center p-3 font-medium text-gray-600">Status</th>
                    <th className="text-center p-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment) => (
                    <tr key={payment.id} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <span className="font-mono text-sm text-gray-600">
                          {payment.transaction_id || payment.id?.substring(0, 8) || 'N/A'}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="font-medium">{payment.customer_name}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        {payment.job_id ? (
                          <span className="text-sm text-gray-600">#{payment.job_id?.substring(0, 8)}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="p-3">
                        {payment.invoice_number ? (
                          <span className="text-sm font-medium text-[#595959]">{payment.invoice_number}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="p-3 text-sm">{formatDate(payment.payment_date || payment.created_at)}</td>
                      <td className="p-3 text-right font-semibold text-green-600">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="p-3 text-center">
                        <Badge variant="outline" className="capitalize">
                          {payment.payment_method_detail || payment.payment_mode?.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        {getSourceBadge(payment.source)}
                      </td>
                      <td className="p-3 text-center">
                        <Badge className={getStatusColor(payment.payment_status)}>
                          {payment.payment_status}
                        </Badge>
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowPaymentDetail(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No payments found</p>
              <p className="text-sm mt-1">Payments will appear here as they are processed</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Manual Payment Dialog */}
      <Dialog open={showAddPayment} onOpenChange={setShowAddPayment}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HandCoins className="w-5 h-5 text-[#595959]" />
              Add Manual Payment
            </DialogTitle>
            <DialogDescription>
              Record an offline payment (Cash, Bank Transfer, Cheque, UPI)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Customer Selection */}
            <div className="space-y-2">
              <Label>Customer *</Label>
              <Select value={selectedCustomerId} onValueChange={handleCustomerSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select customer..." />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {customer.name} - {customer.email}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Job Selection */}
            {customerJobs.length > 0 && (
              <div className="space-y-2">
                <Label>Job (Optional)</Label>
                <Select value={selectedJobId || "none"} onValueChange={(v) => setSelectedJobId(v === "none" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select job..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific job</SelectItem>
                    {customerJobs.map((job) => (
                      <SelectItem key={job.id} value={job.id}>
                        <div className="flex items-center gap-2">
                          <Building className="w-4 h-4" />
                          {job.service_name} - #{job.id?.substring(0, 8)}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Invoice Selection */}
            {customerInvoices.length > 0 && (
              <div className="space-y-2">
                <Label>Invoice (Open invoices only)</Label>
                <Select value={selectedInvoiceId || "none"} onValueChange={(v) => handleInvoiceSelect(v === "none" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select invoice..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific invoice</SelectItem>
                    {customerInvoices.map((inv) => (
                      <SelectItem key={inv.id} value={inv.id}>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          {inv.invoice_number} - Balance: {formatCurrency(inv.balance_due || inv.total_amount)}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {/* Payment Date */}
              <div className="space-y-2">
                <Label>Payment Date *</Label>
                <Input
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                />
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                />
              </div>
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label>Payment Method *</Label>
              <Select 
                value={paymentForm.payment_mode} 
                onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_mode: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">
                    <div className="flex items-center gap-2"><Banknote className="w-4 h-4" />Bank Transfer</div>
                  </SelectItem>
                  <SelectItem value="cheque">
                    <div className="flex items-center gap-2"><Receipt className="w-4 h-4" />Cheque</div>
                  </SelectItem>
                  <SelectItem value="cash">
                    <div className="flex items-center gap-2"><Wallet className="w-4 h-4" />Cash</div>
                  </SelectItem>
                  <SelectItem value="upi">
                    <div className="flex items-center gap-2"><CreditCard className="w-4 h-4" />UPI</div>
                  </SelectItem>
                  <SelectItem value="other">
                    <div className="flex items-center gap-2"><ArrowDownCircle className="w-4 h-4" />Other</div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reference Number */}
            <div className="space-y-2">
              <Label>Reference Number</Label>
              <Input
                placeholder="Cheque no., Transaction ID, etc."
                value={paymentForm.reference_number}
                onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Any additional notes..."
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => { setShowAddPayment(false); resetForm(); }} className="flex-1">
              Cancel
            </Button>
            <Button 
              className="flex-1 bg-[#595959] hover:bg-[#404040]"
              onClick={handleSubmitPayment}
              disabled={submitting || !selectedCustomerId || !paymentForm.amount}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recording...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Record Payment
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Detail Dialog */}
      <Dialog open={showPaymentDetail} onOpenChange={setShowPaymentDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#595959]" />
              Payment Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedPayment && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Amount</p>
                  <p className="text-3xl font-bold text-green-600">{formatCurrency(selectedPayment.amount)}</p>
                </div>
                <div className="text-right">
                  <Badge className={getStatusColor(selectedPayment.payment_status)}>
                    {selectedPayment.payment_status}
                  </Badge>
                  <div className="mt-2">{getSourceBadge(selectedPayment.source)}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Payment ID</p>
                  <p className="font-mono">{selectedPayment.transaction_id || selectedPayment.id?.substring(0, 12)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Date</p>
                  <p>{formatDate(selectedPayment.payment_date || selectedPayment.created_at)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Customer</p>
                  <p className="font-medium">{selectedPayment.customer_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Method</p>
                  <p className="capitalize">{selectedPayment.payment_method_detail || selectedPayment.payment_mode?.replace('_', ' ')}</p>
                </div>
                {selectedPayment.job_id && (
                  <div>
                    <p className="text-gray-500">Job ID</p>
                    <p className="font-mono">#{selectedPayment.job_id?.substring(0, 8)}</p>
                  </div>
                )}
                {selectedPayment.invoice_number && (
                  <div>
                    <p className="text-gray-500">Invoice</p>
                    <p className="font-medium text-[#595959]">{selectedPayment.invoice_number}</p>
                  </div>
                )}
                {selectedPayment.reference_number && (
                  <div>
                    <p className="text-gray-500">Reference</p>
                    <p>{selectedPayment.reference_number}</p>
                  </div>
                )}
                {selectedPayment.entered_by && (
                  <div>
                    <p className="text-gray-500">Entered By</p>
                    <p>{selectedPayment.entered_by}</p>
                  </div>
                )}
              </div>

              {selectedPayment.notes && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Notes</p>
                  <p className="text-sm">{selectedPayment.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
